import { useEffect, useCallback, useState } from 'react'
import { track } from '@ytest/ui'
import type { Message, PendingAction, ToolActivity, ConversationSummary } from '../types'
import type { ChatAction } from './useChatReducer'
import type { ActivePin, ActivePinKind, Integration } from '../external-types'
import type { PortfolioOption as Portfolio } from '../context'
import { useTestOpsCommandConfig } from '../context'
import testOpsService from '../api'

export interface UseChatEffectsConfig {
  conversationIdFromUrl: string | null
  hasStartedChat: boolean
  messages: Message[]
  input: string
  toolActivities: ToolActivity[]
  streamStatus: string | null
  streamingAssistantContent: string
  showIntegrations: boolean
  showPortfolioMenu: boolean
  draftStorageKey: string | null
  dispatchChat: React.Dispatch<ChatAction>
  streamAbortRef: React.MutableRefObject<AbortController | null>
  createdInSessionRef: React.MutableRefObject<Set<string>>
  inputRef: React.RefObject<HTMLTextAreaElement | null>
  messagesEndRef: React.RefObject<HTMLDivElement | null>
  setShowIntegrations: React.Dispatch<React.SetStateAction<boolean>>
  setShowPortfolioMenu: React.Dispatch<React.SetStateAction<boolean>>
  setPendingAction: React.Dispatch<React.SetStateAction<PendingAction | null>>
  setSearchParams: (fn: (prev: URLSearchParams) => URLSearchParams, opts?: { replace?: boolean }) => void
  onPanelStateRestored?: (panelState: Record<string, unknown>) => void
}

export function useChatEffects(config: UseChatEffectsConfig) {
  // Host-injected portfolio fetcher. Each ytest product brings its own
  // (athena reads from athena_portfolios via portfoliosApi; toucan would
  // either provide a mapped equivalent or pass `() => Promise.resolve([])`).
  const { fetchPortfolios } = useTestOpsCommandConfig()

  const {
    conversationIdFromUrl,
    hasStartedChat,
    messages,
    input,
    toolActivities,
    streamStatus,
    streamingAssistantContent,
    showIntegrations,
    showPortfolioMenu,
    draftStorageKey,
    dispatchChat,
    streamAbortRef,
    createdInSessionRef,
    inputRef,
    messagesEndRef,
    setShowIntegrations,
    setShowPortfolioMenu,
    setPendingAction,
    setSearchParams,
  } = config

  // ---------------------------------------------------------------------------
  // Integrations, portfolios, pins, conversation history
  // ---------------------------------------------------------------------------
  const [integrations, setIntegrations] = useState<Integration[]>([])
  const [portfolios, setPortfolios] = useState<Portfolio[]>([])
  const [conversationHistory, setConversationHistory] = useState<ConversationSummary[]>([])
  const [isLoadingHistory, setIsLoadingHistory] = useState(false)
  const [activePins, setActivePins] = useState<ActivePin[]>([])
  const [clearingPinKey, setClearingPinKey] = useState<string | null>(null)

  const loadIntegrations = async () => {
    try {
      const data = await testOpsService.getIntegrations()
      setIntegrations(data)
    } catch (error) {
      console.error('Failed to load integrations:', error)
    }
  }

  const loadPortfolios = async () => {
    try {
      const data = await fetchPortfolios({ limit: 100 })
      setPortfolios(data)
    } catch (error) {
      console.error('Failed to load portfolios:', error)
      setPortfolios([])
    }
  }

  const loadActivePins = useCallback(async () => {
    try {
      const pins = await testOpsService.listActivePins()
      setActivePins(pins)
    } catch (error) {
      console.error('Failed to load active pins:', error)
      setActivePins([])
    }
  }, [])

  const handleClearPin = useCallback(
    async (kind: ActivePinKind, connectionId: string) => {
      const key = `${kind}:${connectionId}`
      setClearingPinKey(key)
      try {
        await testOpsService.clearActivePin(kind, connectionId)
        setActivePins((prev) =>
          prev.filter((p) => !(p.kind === kind && p.connection_id === connectionId)),
        )
      } catch (error) {
        console.error('Failed to clear pin:', error)
      } finally {
        setClearingPinKey(null)
      }
    },
    [],
  )

  const loadConversationHistory = useCallback(async () => {
    setIsLoadingHistory(true)
    try {
      const data = await testOpsService.listConversations(100)
      setConversationHistory(data)
    } catch (error) {
      console.error('Failed to load conversation history:', error)
      setConversationHistory([])
    } finally {
      setIsLoadingHistory(false)
    }
  }, [])

  const hydrateConversation = useCallback((
    conversation: Awaited<ReturnType<typeof testOpsService.getConversation>>
  ) => {
    setPendingAction(conversation.pending_action)
    dispatchChat({
      type: 'hydrate-conversation',
      payload: {
        conversationId: conversation.id,
        portfolioId: conversation.portfolio_id || '',
        messages: conversation.messages.map((message) => ({
          id: message.id,
          role: message.role,
          content: message.content,
          timestamp: new Date(message.created_at),
          sources: message.sources || [],
          toolCalls: message.tool_calls || undefined,
          feedback: (message as any).feedback || null,
        })),
      }
    })
    // Return panel_state so the caller can restore the panel
    return conversation.panel_state || null
  }, [])

  // ---------------------------------------------------------------------------
  // Effect: Load initial data
  // ---------------------------------------------------------------------------
  useEffect(() => {
    loadIntegrations()
    loadPortfolios()
    void loadActivePins()
    void loadConversationHistory()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ---------------------------------------------------------------------------
  // Effect: Abort stream on unmount
  // ---------------------------------------------------------------------------
  useEffect(() => {
    return () => { streamAbortRef.current?.abort() }
  }, [])

  // ---------------------------------------------------------------------------
  // Effect: Sync conversationIdFromUrl
  // ---------------------------------------------------------------------------
  useEffect(() => {
    dispatchChat({ type: 'set-active-conversation', payload: conversationIdFromUrl })
  }, [conversationIdFromUrl])

  // ---------------------------------------------------------------------------
  // Effect: Restore conversation from URL
  // ---------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false

    const restoreConversation = async () => {
      if (!conversationIdFromUrl) {
        if (!cancelled) {
          dispatchChat({ type: 'restore-empty' })
          dispatchChat({ type: 'set-restoring', payload: false })
        }
        return
      }

      // Skip hydration for conversations just created in this session — messages
      // are already in local state and hydrating would race with the AI append.
      if (createdInSessionRef.current.has(conversationIdFromUrl)) {
        if (!cancelled) {
          dispatchChat({ type: 'set-restoring', payload: false })
        }
        return
      }

      dispatchChat({ type: 'set-restoring', payload: true })
      try {
        const conversation = await testOpsService.getConversation(conversationIdFromUrl)
        if (cancelled) return
        const panelState = hydrateConversation(conversation)
        if (panelState && config.onPanelStateRestored) {
          config.onPanelStateRestored(panelState as Record<string, unknown>)
        }
        track('TestOps Conversation Restored', {
          conversationId: conversationIdFromUrl,
          messageCount: conversation.messages.length,
          hasPanelState: !!panelState,
        })
      } catch (error) {
        console.error('Failed to restore TestOps Hub conversation:', error)
        if (cancelled) return
        dispatchChat({ type: 'restore-empty' })
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev)
          next.delete('conversation')
          return next
        }, { replace: true })
      } finally {
        if (!cancelled) {
          dispatchChat({ type: 'set-restoring', payload: false })
        }
      }
    }

    void restoreConversation()

    return () => {
      cancelled = true
    }
  }, [conversationIdFromUrl, hydrateConversation, setSearchParams])

  // ---------------------------------------------------------------------------
  // Effect: Close integrations dropdown when clicking outside
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (!target.closest('.integrations-dropdown') && !target.closest('.integrations-button')) {
        setShowIntegrations(false)
      }
    }

    if (showIntegrations) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showIntegrations])

  // ---------------------------------------------------------------------------
  // Effect: Close portfolio dropdown when clicking outside
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (!target.closest('.portfolio-dropdown') && !target.closest('.portfolio-button')) {
        setShowPortfolioMenu(false)
      }
    }

    if (showPortfolioMenu) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showPortfolioMenu])

  // ---------------------------------------------------------------------------
  // Effect: Auto-scroll to bottom
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (hasStartedChat) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, hasStartedChat, toolActivities, streamStatus, streamingAssistantContent])

  // ---------------------------------------------------------------------------
  // Effect: Reset textarea height
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto'
    }
  }, [hasStartedChat, input])

  // ---------------------------------------------------------------------------
  // Effect: Draft restore
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!draftStorageKey) {
      dispatchChat({ type: 'set-input', payload: '' })
      return
    }
    const savedDraft = sessionStorage.getItem(draftStorageKey)
    if (savedDraft !== null) {
      dispatchChat({ type: 'set-input', payload: savedDraft })
      return
    }

    dispatchChat({ type: 'set-input', payload: '' })
  }, [draftStorageKey])

  // ---------------------------------------------------------------------------
  // Effect: Draft persist
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!draftStorageKey) return
    const normalized = input.trim()
    if (!normalized) {
      sessionStorage.removeItem(draftStorageKey)
      return
    }

    sessionStorage.setItem(draftStorageKey, input)
  }, [draftStorageKey, input])

  return {
    integrations,
    portfolios,
    conversationHistory,
    setConversationHistory,
    isLoadingHistory,
    activePins,
    clearingPinKey,
    loadIntegrations,
    loadConversationHistory,
    loadActivePins,
    handleClearPin,
    hydrateConversation,
  }
}
